import type { SkillBranch } from '@sorrem/shared';

/**
 * Every id the skill tree builds from its parts, in one place: the ids the
 * tree selects and hovers by, the keys its links are scheduled by, and the
 * ids of the SVG defs (clips, masks, filters) its pieces reference. Build
 * them only through these functions; nothing should parse one back apart.
 */

/** The id the sigil uses for selection; selecting it opens the branch overview. */
export const SIGIL_SELECTION_ID = 'sigil';

/** The id dials use for hover and selection, so they never collide with skill ids. */
export function toDialSelectionId(branch: SkillBranch): string {
    return `dial:${branch}`;
}

/** The id grouping roots use for hover and selection, so they never collide with skill ids. */
export function toGroupSelectionId(groupId: string): string {
    return `group:${groupId}`;
}

/** The key of the link from the sigil to a branch's dial. */
export function toSigilLinkKey(branch: SkillBranch): string {
    return `sigil-${branch}`;
}

/** The key of the link from a branch's dial to grouping root `groupId`. */
export function toGroupLinkKey(groupId: string): string {
    return `group-${groupId}`;
}

/** The key of the link from its grouping root to skill `skillId`. */
export function toSkillLinkKey(skillId: string): string {
    return skillId;
}

/** The part of the tree an effect lights, which times it during the intro. */
export type SkillTreeEffectTarget =
    | { readonly kind: 'sigil' }
    | { readonly kind: 'dial'; readonly branch: SkillBranch }
    | { readonly kind: 'skill'; readonly id: string };

/**
 * The ids of the scene's SVG defs. SVG ids are global to the document, so
 * each scene prefixes its own (see `SkillTreeScene`), keeping two trees, or
 * a tree and a skill icon, from resolving each other's defs.
 */
export interface SkillTreeSvgIds {
    /** The drop shadow under every label. */
    readonly textShadow: string;
    /** The soft blur under every line-art emblem's strokes. */
    readonly emblemGlow: string;
    /** The mist mask over the Essence links; its patterns and blur extend it. */
    readonly mistMask: string;
    /** The mask that draws link `linkKey` out from its parent during the intro. */
    readonly linkReveal: (linkKey: string) => string;
    /** The clip that shows a skill's partly filled marker. */
    readonly nodeClip: (skillId: string) => string;
    /** The clip that shows a dial's fill up to its percent. */
    readonly dialClip: (branch: SkillBranch) => string;
    /** The mask that sweeps a dial's values in during the intro. */
    readonly dialSweep: (branch: SkillBranch) => string;
}

/** The scene's SVG def ids under `prefix`, which must be unique in the document (e.g. from `useId`). */
export function buildSkillTreeSvgIds(prefix: string): SkillTreeSvgIds {
    return {
        textShadow: `${prefix}-text-shadow`,
        emblemGlow: `${prefix}-emblem-glow`,
        mistMask: `${prefix}-mist-mask`,
        linkReveal: (linkKey) => `${prefix}-intro-link-${linkKey}`,
        nodeClip: (skillId) => `${prefix}-partial-${skillId}`,
        dialClip: (branch) => `${prefix}-dial-fill-${branch}`,
        dialSweep: (branch) => `${prefix}-intro-sweep-dial-${branch}`,
    };
}

/** An SVG id prefix from React's `useId`, without the colons it wraps ids in. */
export function toSvgIdPrefix(reactId: string, name: string): string {
    return `${name}-${reactId.replace(/:/g, '')}`;
}
